import { z } from "zod";

export const CouponSchema = z.object({
  code: z.string()
    .min(3, { message: "Coupon code must be at least 3 characters" })
    .max(50, { message: "Coupon code cannot exceed 50 characters" })
    .regex(/^[a-zA-Z0-9_-]+$/, { 
      message: "Coupon code can only contain letters, numbers, hyphens, and underscores" 
    }),

  category: z.string()
    .min(1, { message: "Category is required" }),

  description: z.string()
    .min(10, { message: "Description must be at least 10 characters" })
    .max(500, { message: "Description cannot exceed 500 characters" }),

  discount_type: z.enum(["percentage", "amount"], {
    errorMap: () => ({ message: "Discount type must be either 'percentage' or 'amount'" })
  }),

  discount_applies_above: z.number()
    .min(0, { message: "Minimum order amount must be 0 or higher" })
    .optional(),

  discount_value: z.number()
    .positive({ message: "Discount value must be a positive number" })
    .max(1000, { message: "Discount value cannot exceed 1000" }),

  start_date: z.coerce.date()
    .min(new Date(), { message: "Start date cannot be in the past" }),

  expiry_date: z.coerce.date()
    .min(new Date(), { message: "Expiry date cannot be in the past" }),

  status: z.enum(["active", "inactive"], {
    errorMap: () => ({ message: "Status must be either 'active' or 'inactive'" })
  }),

  inserted_at: z.coerce.date().optional(),
  updated_at: z.coerce.date().optional(),
})
.refine(data => data.expiry_date > data.start_date, {
  message: "Expiry date must be after start date",
  path: ["expiry_date"]
})
.refine(data => {
  if (data.discount_type === "percentage") {
    return data.discount_value <= 100;
  }
  return true;
}, {
  message: "Percentage discount cannot exceed 100%",
  path: ["discount_value"]
});

export type ICoupon = z.infer<typeof CouponSchema>;
